/* __next_internal_client_entry_do_not_use__ SDUIGridV2FixedColumnLayoutBase auto */ import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getSlateSpacingPixels } from "@faire/retailer-shared/SlateMappings/getSlateSpacingPixels";
import { Column } from "@faire/web--source/slate/Layout";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import { IGridStyle } from "@faire/web-api--source/indigofair/data/IGridStyle";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
// eslint-disable-next-line no-restricted-imports
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import React, { useContext } from "react";
import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/retailer/components/SDUI/__internal__/Styling/responsiveValues";
import * as v2GridStyles from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/GridV2/v2Styles.css";
import { SDUIGridHeaderRow } from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridHeaderRow";
import * as gridStyles from "@faire/retailer/components/SDUI/ComponentRenderers/Grid/__internal__/styles.css";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { FilterablePaginatedSectionContext } from "@faire/retailer/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/FilterablePaginatedSectionContext";
import { GridV2BrandTilePlaceholder, GridV2GenericTilePlaceholder } from "./GridV2Placeholders";
import { GridV2ProductTilePlaceholder } from "./GridV2Placeholders";
import { SDUIGridV2Tile } from "./SDUIGridV2Tile";
import { getGridFixedLayoutResponsiveStyleVars } from "./v2FixedLayoutStylingUtils";
var FALLBACK_NUMBER_OF_TILES_PER_ROW = 2;
export var SDUIGridV2FixedColumnLayoutBase = function(param) {
    var gridComponent = param.gridComponent, isRequiredForInteractivity = param.isRequiredForInteractivity, loggingData = param.loggingData, renderBehaviour = param.renderBehaviour;
    var _gridComponent_column_layout = gridComponent.column_layout, column_layout = _gridComponent_column_layout === void 0 ? IGridComponent.IColumnLayout.build() : _gridComponent_column_layout, header_background_icon = gridComponent.header_background_icon, responsive_header_background_icon = gridComponent.responsive_header_background_icon, responsive_title = gridComponent.responsive_title, _gridComponent_style = gridComponent.style, style = _gridComponent_style === void 0 ? IGridStyle.build() : _gridComponent_style, tiles = gridComponent.tiles, title = gridComponent.title, log_tile_batch_view = gridComponent.log_tile_batch_view;
    var _column_layout_fixed;
    var _ref = (_column_layout_fixed = column_layout.fixed) !== null && _column_layout_fixed !== void 0 ? _column_layout_fixed : IGridComponent.IFixedColumnLayout.build(), _ref_num_columns = _ref.num_columns, num_columns = _ref_num_columns === void 0 ? FALLBACK_NUMBER_OF_TILES_PER_ROW : _ref_num_columns, responsive_num_columns = _ref.responsive_num_columns;
    var _style_column_spacing = style.column_spacing, column_spacing = _style_column_spacing === void 0 ? Spacing.SPACING_NONE : _style_column_spacing, _style_row_spacing = style.row_spacing, row_spacing = _style_row_spacing === void 0 ? Spacing.SPACING_NONE : _style_row_spacing;
    var columnSpacingPixels = getSlateSpacingPixels(column_spacing);
    var rowSpacingPixels = getSlateSpacingPixels(row_spacing);
    var responsiveNumberOfTilesPerRow = convertServerResponsivePropertyToClientResponsiveProperty(num_columns, function(col) {
        return col;
    }, responsive_num_columns);
    var numTilesPerRow = useResponsiveValue(responsiveNumberOfTilesPerRow);
    var _obj;
    return /*#__PURE__*/ _jsxs(Column, {
        className: gridStyles.SDUIGridWrapper,
        children: [
            /*#__PURE__*/ _jsx(SDUIGridHeaderRow, {
                title: title,
                responsiveTitle: responsive_title,
                backgroundIcon: header_background_icon,
                responsiveBackgroundIcon: responsive_header_background_icon,
                loggingData: loggingData
            }),
            /*#__PURE__*/ _jsx("div", {
                className: classNames(v2GridStyles.gridTileContainer, v2GridStyles.gridFixedLayout),
                style: assignInlineVars(_object_spread((_obj = {}, _define_property(_obj, v2GridStyles.varRowGap, "".concat(rowSpacingPixels, "px")), _define_property(_obj, v2GridStyles.varColumnGap, "".concat(columnSpacingPixels, "px")), _obj), getGridFixedLayoutResponsiveStyleVars(responsiveNumberOfTilesPerRow))),
                children: tiles.map(function(tile, index) {
                    // first row should be visible for UPLT calculations
                    var shouldForceVisibility = index < numTilesPerRow && isRequiredForInteractivity;
                    return /*#__PURE__*/ _jsx(MaybeWhenVisibilityIsImminent, {
                        forceVisibility: shouldForceVisibility,
                        placeholder: getRowPlaceholder(tile),
                        renderBehaviour: renderBehaviour,
                        children: /*#__PURE__*/ _jsx(SDUIGridV2Tile, {
                            tile: tile,
                            loggingData: loggingData,
                            logTileBatchView: log_tile_batch_view,
                            rowSpacingPixels: rowSpacingPixels,
                            shouldSetIsScrollingTopToFalseOnView: index === 0 && isRequiredForInteractivity && (renderBehaviour === null || renderBehaviour === void 0 ? void 0 : renderBehaviour.gridOptimizations)
                        })
                    }, getTileKey(tile, index));
                })
            })
        ]
    });
};
var getTileKey = function(tile, index) {
    var _tile_logging_data, _tile_logging_data1;
    return tile.logging_data ? "".concat((_tile_logging_data = tile.logging_data) === null || _tile_logging_data === void 0 ? void 0 : _tile_logging_data.instance_identifier, "-").concat((_tile_logging_data1 = tile.logging_data) === null || _tile_logging_data1 === void 0 ? void 0 : _tile_logging_data1.tile_position) : index;
};
var getRowPlaceholder = function(tile) {
    if (!tile) {
        return undefined;
    }
    if (tile.brand_tile || tile.brand_tile_with_action) {
        return /*#__PURE__*/ _jsx(GridV2BrandTilePlaceholder, {});
    } else if (tile.product_tile || tile.product_with_brand_info_tile) {
        return /*#__PURE__*/ _jsx(GridV2ProductTilePlaceholder, {});
    }
    return /*#__PURE__*/ _jsx(GridV2GenericTilePlaceholder, {});
};
var MaybeWhenVisibilityIsImminent = function(param) {
    var forceVisibility = param.forceVisibility, placeholder = param.placeholder, children = param.children, renderBehaviour = param.renderBehaviour;
    var isScrollingToTop = useContext(FilterablePaginatedSectionContext).isScrollingToTop;
    if (!forceVisibility && isScrollingToTop && (renderBehaviour === null || renderBehaviour === void 0 ? void 0 : renderBehaviour.gridOptimizations)) {
        if (placeholder) {
            return placeholder;
        }
    }
    if (!forceVisibility && placeholder) {
        return /*#__PURE__*/ _jsx(WhenVisibilityIsImminent, {
            customizePlaceholder: placeholder,
            children: children
        });
    }
    return children;
};
